/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useState} from 'react';
import debounce from 'lodash.debounce';
import useHasMounted from './useHasMounted';

export interface UseElementResizeResult {
  width: number;
  height: number;
};

/**
 * Defining useElementResize hook. The useElementResize is a custom hook 
 * which handles resize event listener for the particular element. This hook
 * will returns container width and height.
 * 
 * ```typescript
 *  
 *  import {useElementResize} from '@ion/core/src/hooks/useElementResize';
 * 
 *  useElementResize('dlsMapContainer', (event) => {
 *    // code that will be executed when the resize...
 *  });
 * 
 * ```
 */
export const useElementResize = (elementId: string, callbackFn?: Function) => {
  const [dimentions, setDimentions] = useState({});
  const isMounted                   = useHasMounted();
  const container                   = elementId && document.getElementById(elementId);

  const eventListener = useCallback(debounce((container, e) => {
    const {offsetWidth: width, offsetHeight: height} = container;
    setDimentions({width, height});
    callbackFn?.(e, container);
  }, 300), []);

  useEffect(() => {
    if (isMounted) {
      let observer;
      if (container) {
        observer  = new ResizeObserver((e) => {
          eventListener(container, e);
        });
        observer.observe(container);
        eventListener(container, null);
      }

      // unsubscribe the observer for the element...
      return () => {
        observer?.disconnect();
      };
    }
  }, [isMounted, container]);

  return dimentions as UseElementResizeResult;
};

export default useElementResize;
